import { useOnStateChanged } from "@uirouter/react";
import React, {
  createContext,
  FunctionComponent,
  ReactNode,
  useCallback,
  useState,
} from "react";
import { Modal, ModalProps } from ".";

type BaseOverlayType = {
  title?: ModalProps["title"];
  onClose?: () => Promise<void>;
};

type ChildrenOverlayType = BaseOverlayType & {
  children: ReactNode;
};

type PortalOverlayType = BaseOverlayType & {
  portalUUID: string; //
};

type OverlayType = ChildrenOverlayType | PortalOverlayType;

function isPortalOverlay(x: OverlayType): x is PortalOverlayType {
  return (x as any).portalUUID;
}

export interface GlobalOverlayContextType {
  setOverlay: React.Dispatch<React.SetStateAction<OverlayType>>;
  closeOverlay: () => void;
  getRef?: (uuid: string) => HTMLDivElement | undefined;
}

export const GlobalOverlayContext = createContext<GlobalOverlayContextType>(
  (undefined as any) as GlobalOverlayContextType
);

export const GlobalOverlayProvider: FunctionComponent<{}> = ({ children }) => {
  const [current, setOverlay] = useState<OverlayType>({
    children: null,
    title: "",
  });

  const closeOverlay = useCallback(() => {
    setOverlay({ children: null });
  }, []);

  useOnStateChanged(() => {
    if (isPortalOverlay(current) || current.children !== null) {
      closeOverlay();
    }
  });

  const [element, setElement] = useState<HTMLDivElement>();

  const getRef = useCallback(
    (uuid: string) =>
      isPortalOverlay(current) && current.portalUUID === uuid
        ? element
        : undefined,
    [current, element]
  );

  return (
    <GlobalOverlayContext.Provider
      value={{
        setOverlay,
        getRef,
        closeOverlay,
      }}
    >
      {children}
      {!isPortalOverlay(current) && !current.children ? null : (
        <Modal
          title={current.title}
          onDismiss={() =>
            (current.onClose?.() || Promise.resolve()).then(() =>
              setOverlay({ children: null })
            )
          }
        >
          {isPortalOverlay(current) ? (
            <div
              ref={(node) => {
                if (node) setElement(node);
              }}
            ></div>
          ) : (
            current.children
          )}
        </Modal>
      )}
    </GlobalOverlayContext.Provider>
  );
};
